import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import { attachRosTopicWithRetry } from "lib/ros/rosTopicSubscribeWithRetry";
import {
    E_STOP_SUBSCRIBE_QOS,
    E_STOP_TOPIC,
    ESTOP_BOOL_MSG_TYPE,
    HARDWARE_ESTOP_TOPIC,
    SOFTWARE_ESTOP_TOPIC,
    parseEstopTopicPayload,
    publishSoftwareEstop,
    setEstopService,
} from "lib/protocols/estop";

const ESTOP_BOOL_TOPIC_OPTIONS = {
    messageTypeFallback: ESTOP_BOOL_MSG_TYPE,
    subscribeQos: E_STOP_SUBSCRIBE_QOS,
    markMessageReceived: (data) => parseEstopTopicPayload(data) !== null,
};

const SOFTWARE_ESTOP_STORAGE_KEY = "estop.softwareEngaged";

const readStoredSoftwareEstop = () => {
    try {
        return sessionStorage.getItem(SOFTWARE_ESTOP_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
};

const writeStoredSoftwareEstop = (engaged) => {
    try {
        if (engaged) {
            sessionStorage.setItem(SOFTWARE_ESTOP_STORAGE_KEY, "1");
        } else {
            sessionStorage.removeItem(SOFTWARE_ESTOP_STORAGE_KEY);
        }
    } catch {
        // Ignore private browsing / quota errors.
    }
};

export const EstopContext = createContext();
EstopContext.displayName = "Estop";
export const useEstop = () => useContext(EstopContext);

const isSetEstopServiceUnavailable = (message) => {
    const raw = String(message || "");
    if (/Service\s+\S+\s+is not available/i.test(raw)) {
        return true;
    }
    return (
        /set_estop/i.test(raw) &&
        (/does not exist/i.test(raw) ||
            /not found/i.test(raw) ||
            /not available/i.test(raw) ||
            /unavailable/i.test(raw) ||
            /unable to communicate/i.test(raw))
    );
};

const isRobotUnreachableEstopFailure = (message) => {
    const raw = String(message || "");
    return (
        isSetEstopServiceUnavailable(raw) ||
        /robot not connected/i.test(raw) ||
        raw === "Service caller not initialized"
    );
};

/** @returns {'off' | 'hardware' | 'software' | 'local' | 'unknown'} */
const deriveEstopSource = (
    displayEngaged,
    hardwareEngaged,
    rosEngaged,
    softwareEngaged,
    localSoftwareEngaged
) => {
    if (displayEngaged !== true) {
        return "off";
    }
    if (hardwareEngaged === true) {
        return "hardware";
    }
    if (softwareEngaged === true || rosEngaged === true) {
        return "software";
    }
    if (localSoftwareEngaged) {
        return "local";
    }
    return "unknown";
};

const EstopProvider = ({ children }) => {
    const { t } = useTranslation("control");
    const { controlSocket, connectionState, callService, sendCommand } = useARC();
    const { addToast } = useToast();
    /** Engaged state from `/state/estop` when the robot stack is reachable. */
    const [rosEstopEngaged, setRosEstopEngaged] = useState(null);
    /** Physical latch from `/hardware/estop` (r2c2_controlboard). */
    const [hardwareEstopEngaged, setHardwareEstopEngaged] = useState(null);
    /** Software latch from `/software/estop` (r2c2_controlboard, transient-local). */
    const [softwareEstopEngaged, setSoftwareEstopEngaged] = useState(() =>
        readStoredSoftwareEstop() ? true : null
    );
    /** UI-held software E-STOP when `/set_estop` is unavailable or ARC is offline. */
    const [localSoftwareEstop, setLocalSoftwareEstop] = useState(() => readStoredSoftwareEstop());
    /** Immediate toggle feedback until ROS topics catch up. */
    const [uiEstopOverride, setUiEstopOverride] = useState(null);
    /** True while a UI toggle is in flight (button should ignore clicks). */
    const [toggleBusy, setToggleBusy] = useState(false);
    /** `null` = unknown, `true`/`false` = probed or learned from a failed call. */
    const setEstopServiceAvailableRef = useRef(null);
    /** Ignores stale `/set_estop` results when the user toggles again during a hang. */
    const toggleSeqRef = useRef(0);
    /** Latest UI intent; software/ros topic updates that disagree are ignored while set. */
    const pendingIntentRef = useRef(null);
    const toggleBusyRef = useRef(false);
    const unlockTimerRef = useRef(null);
    const pendingClearTimerRef = useRef(null);

    const topicEstopEngaged = useMemo(
        () =>
            rosEstopEngaged === true ||
            hardwareEstopEngaged === true ||
            softwareEstopEngaged === true ||
            localSoftwareEstop,
        [rosEstopEngaged, hardwareEstopEngaged, softwareEstopEngaged, localSoftwareEstop]
    );

    const estopEngaged = uiEstopOverride !== null ? uiEstopOverride : topicEstopEngaged;

    const clearToggleBusy = useCallback(() => {
        if (unlockTimerRef.current != null) {
            window.clearTimeout(unlockTimerRef.current);
            unlockTimerRef.current = null;
        }
        toggleBusyRef.current = false;
        setToggleBusy(false);
    }, []);

    useEffect(() => {
        if (uiEstopOverride === null) {
            return;
        }
        if (topicEstopEngaged === uiEstopOverride) {
            // Drop display override once topics agree. Busy + pendingIntent stay
            // until settle timers so rapid clicks cannot re-enter and stale
            // opposite echoes cannot flip the UI.
            setUiEstopOverride(null);
        }
    }, [topicEstopEngaged, uiEstopOverride]);

    useEffect(
        () => () => {
            if (unlockTimerRef.current != null) {
                window.clearTimeout(unlockTimerRef.current);
            }
            if (pendingClearTimerRef.current != null) {
                window.clearTimeout(pendingClearTimerRef.current);
            }
        },
        []
    );

    const estopSource = useMemo(
        () =>
            deriveEstopSource(
                estopEngaged,
                hardwareEstopEngaged,
                rosEstopEngaged,
                softwareEstopEngaged,
                localSoftwareEstop
            ),
        [
            estopEngaged,
            hardwareEstopEngaged,
            rosEstopEngaged,
            softwareEstopEngaged,
            localSoftwareEstop,
        ]
    );

    const canReleaseFromUi = estopEngaged === true && hardwareEstopEngaged !== true;

    useEffect(() => {
        setEstopServiceAvailableRef.current = null;
        if (connectionState !== ConnectionState.Connected || !controlSocket) {
            setRosEstopEngaged(null);
            setHardwareEstopEngaged(null);
            setSoftwareEstopEngaged(readStoredSoftwareEstop() ? true : null);
            return;
        }

        const handleSoftwareOrRosPayload = (setter, parsed, persistSoftware = false) => {
            // While a UI toggle is pending, ignore out-of-order echoes that would
            // flip the button against the latest intent (spam-click oscillation).
            if (pendingIntentRef.current !== null && parsed !== pendingIntentRef.current) {
                return;
            }
            setter(parsed);
            if (persistSoftware) {
                writeStoredSoftwareEstop(parsed);
            }
            if (parsed) {
                setLocalSoftwareEstop(false);
            }
        };

        const cleanupState = attachRosTopicWithRetry(
            controlSocket,
            E_STOP_TOPIC,
            (data) => {
                const parsed = parseEstopTopicPayload(data);
                if (parsed !== null) {
                    handleSoftwareOrRosPayload(setRosEstopEngaged, parsed);
                }
            },
            ESTOP_BOOL_TOPIC_OPTIONS
        );

        const cleanupSoftware = attachRosTopicWithRetry(
            controlSocket,
            SOFTWARE_ESTOP_TOPIC,
            (data) => {
                const parsed = parseEstopTopicPayload(data);
                if (parsed !== null) {
                    handleSoftwareOrRosPayload(setSoftwareEstopEngaged, parsed, true);
                }
            },
            ESTOP_BOOL_TOPIC_OPTIONS
        );

        const cleanupHardware = attachRosTopicWithRetry(
            controlSocket,
            HARDWARE_ESTOP_TOPIC,
            (data) => {
                const parsed = parseEstopTopicPayload(data);
                if (parsed !== null) {
                    setHardwareEstopEngaged(parsed);
                }
            },
            ESTOP_BOOL_TOPIC_OPTIONS
        );

        return () => {
            cleanupState();
            cleanupSoftware();
            cleanupHardware();
        };
    }, [controlSocket, connectionState]);

    const publishSoftwareEstopTopic = useCallback(
        (engaged) => {
            if (connectionState !== ConnectionState.Connected || !controlSocket) {
                return false;
            }
            try {
                sendCommand(publishSoftwareEstop(engaged));
                return true;
            } catch (e) {
                console.warn("[EstopProvider] Failed to publish /software/estop:", e);
                return false;
            }
        },
        [controlSocket, connectionState, sendCommand]
    );

    /** Push local software E-STOP to the robot when the stack comes online. */
    useEffect(() => {
        if (connectionState !== ConnectionState.Connected || !localSoftwareEstop) {
            return;
        }

        let cancelled = false;

        void (async () => {
            publishSoftwareEstopTopic(true);
            if (setEstopServiceAvailableRef.current === false) {
                return;
            }
            try {
                const res = await callService(...setEstopService(true));
                if (cancelled) {
                    return;
                }
                if (res && res.success === false) {
                    return;
                }
                setLocalSoftwareEstop(false);
            } catch (e) {
                if (isSetEstopServiceUnavailable(e instanceof Error ? e.message : "")) {
                    setEstopServiceAvailableRef.current = false;
                    return;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [callService, connectionState, localSoftwareEstop, publishSoftwareEstopTopic]);

    const requestRobotEstop = useCallback(
        async (engaged) => {
            if (connectionState !== ConnectionState.Connected) {
                return false;
            }
            if (setEstopServiceAvailableRef.current === false) {
                return false;
            }
            try {
                const res = await callService(...setEstopService(engaged));
                if (res && res.success === false) {
                    // Bridge answered but robot offline / reject — soft fail, no ROS toast.
                    return false;
                }
                setEstopServiceAvailableRef.current = true;
                return true;
            } catch (e) {
                const raw = e instanceof Error ? e.message : "set_estop failed";
                if (isRobotUnreachableEstopFailure(raw)) {
                    if (isSetEstopServiceUnavailable(raw)) {
                        setEstopServiceAvailableRef.current = false;
                    }
                    return false;
                }
                throw e;
            }
        },
        [callService, connectionState]
    );

    const toggleEstop = useCallback(async () => {
        if (toggleBusyRef.current) {
            return;
        }

        const engaged = estopEngaged === true;

        if (engaged && hardwareEstopEngaged === true) {
            addToast({
                type: "warning",
                message: t("estop.latchedHardware"),
            });
            return;
        }

        const next = !engaged;
        const seq = ++toggleSeqRef.current;
        toggleBusyRef.current = true;
        setToggleBusy(true);
        pendingIntentRef.current = next;
        setUiEstopOverride(next);
        // Apply /software/estop immediately; do not block the button on /set_estop.
        publishSoftwareEstopTopic(next);
        writeStoredSoftwareEstop(next);
        if (next) {
            setSoftwareEstopEngaged(true);
            setRosEstopEngaged(true);
        } else if (hardwareEstopEngaged !== true) {
            setSoftwareEstopEngaged(false);
            setRosEstopEngaged(false);
            setLocalSoftwareEstop(false);
        }

        // Serialize clicks briefly; keep ignoring contradicting topic echoes longer.
        if (unlockTimerRef.current != null) {
            window.clearTimeout(unlockTimerRef.current);
        }
        if (pendingClearTimerRef.current != null) {
            window.clearTimeout(pendingClearTimerRef.current);
        }
        unlockTimerRef.current = window.setTimeout(() => {
            if (seq !== toggleSeqRef.current) {
                return;
            }
            clearToggleBusy();
        }, 450);
        pendingClearTimerRef.current = window.setTimeout(() => {
            if (seq !== toggleSeqRef.current) {
                return;
            }
            if (pendingIntentRef.current === next) {
                pendingIntentRef.current = null;
            }
            pendingClearTimerRef.current = null;
        }, 1200);

        try {
            const synced = await requestRobotEstop(next);
            if (seq !== toggleSeqRef.current) {
                return;
            }
            if (synced) {
                setLocalSoftwareEstop(false);
            } else if (!next) {
                setLocalSoftwareEstop(false);
            } else {
                setLocalSoftwareEstop(true);
                addToast({
                    type: "warning",
                    message: t("estop.robotNotConnected"),
                });
            }
        } catch (e) {
            if (seq !== toggleSeqRef.current) {
                return;
            }
            if (next) {
                setLocalSoftwareEstop(true);
            }
            addToast({
                type: "warning",
                message: t("estop.robotNotConnected"),
            });
        }
    }, [
        addToast,
        clearToggleBusy,
        estopEngaged,
        hardwareEstopEngaged,
        publishSoftwareEstopTopic,
        requestRobotEstop,
        t,
    ]);

    return (
        <EstopContext.Provider
            value={{
                /** @deprecated use estopEngaged */
                estopPressed: estopEngaged,
                estopEngaged,
                rosEstopEngaged,
                hardwareEstopEngaged,
                softwareEstopEngaged,
                localSoftwareEstop,
                estopSource,
                canReleaseFromUi,
                toggleBusy,
                toggleEstop,
            }}
        >
            {children}
        </EstopContext.Provider>
    );
};

export default EstopProvider;
